import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FormProvider, useForm } from "react-hook-form";
import { LuEye, LuEyeOff } from "react-icons/lu";
import { useAcceptInviteMutation, usePeekInviteQuery } from "../../app/features/auth/authApi";
import { Field, FormBanner, useApiFormErrors } from "../../utils/forms";
import SEOData from "../../components/SEO/SEO";
import LoadingUi from "../../components/LoadingUi/LoadingUi";

const GRADIENT = "linear-gradient(135deg,#8B1FD9,#C026D3)";
const labelCls = "text-[11px] font-semibold text-slate-500 uppercase tracking-wide";
const inputCls = "rounded-xl border-slate-200 bg-slate-50 text-sm text-slate-700 focus:border-violet-400 focus:ring-violet-200 pr-11";

const Card = ({ children }) => (
  <div className="flex min-h-[calc(100vh-80px)] items-center justify-center bg-gradient-to-br from-violet-50 via-white to-slate-50 px-4 py-10">
    <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
      <div className="h-24 flex items-center justify-center" style={{ background: GRADIENT }}>
        <div className="w-16 h-16 rounded-full bg-white/15 backdrop-blur flex items-center justify-center">
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
            <line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" />
          </svg>
        </div>
      </div>
      <div className="px-7 py-6">{children}</div>
    </div>
  </div>
);

/**
 * Landing page for invitation emails (new UniEdu staff, partner staff,
 * approved partner owners): choose your first password, then sign in.
 */
const AcceptInvite = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [show, setShow] = useState(false);

  const { data: invite, isLoading, isError } = usePeekInviteQuery(token, { skip: !token });
  const [acceptInvite, { isLoading: saving }] = useAcceptInviteMutation();

  const methods = useForm({ mode: "onTouched", defaultValues: { password: "", confirm_password: "" } });
  const { handleSubmit, setError, getValues } = methods;
  const { handleApiError } = useApiFormErrors(setError);

  const onSubmit = async ({ password }) => {
    try {
      const res = await acceptInvite({ token, password }).unwrap();
      toast.success("Your password is set. Please sign in.");
      navigate(`/sign-in?email=${encodeURIComponent(res.email || invite?.email || "")}`, { replace: true });
    } catch (err) {
      handleApiError(err, { knownFields: ["password"], fallbackMessage: "Could not set your password." });
    }
  };

  if (token && isLoading) return <LoadingUi />;

  if (!token || isError) {
    return (
      <Card>
        <SEOData title="Invitation" description="Accept your UniEdu invitation." canonical={window.location.pathname} />
        <h1 className="text-xl font-semibold text-slate-800 text-center">This link doesn&apos;t work</h1>
        <p className="mt-2 text-sm text-slate-500 text-center leading-relaxed">
          Invitation links work once and expire after 72 hours. Ask the person who invited you to
          resend it. If you&apos;ve already set your password, just sign in.
        </p>
        <Link to="/sign-in"
          className="mt-6 block w-full py-3 rounded-xl text-center text-white font-semibold shadow-lg shadow-violet-200 hover:opacity-90"
          style={{ background: GRADIENT }}>
          Go to sign in
        </Link>
      </Card>
    );
  }

  const eye = (
    <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? "Hide password" : "Show password"}
      className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-slate-400 hover:text-slate-600">
      {show ? <LuEyeOff size={18} /> : <LuEye size={18} />}
    </button>
  );

  return (
    <Card>
      <SEOData title="Set your password" description="Accept your UniEdu invitation." canonical={window.location.pathname} />
      <h1 className="text-xl font-semibold text-slate-800 text-center">
        Welcome{invite?.first_name ? `, ${invite.first_name}` : ""}!
      </h1>
      <p className="mt-1 mb-5 text-sm text-slate-500 text-center">
        You&apos;ve been invited to <span className="font-semibold text-slate-700">{invite?.organization}</span>.
        Set a password for <span className="font-semibold text-slate-700">{invite?.email}</span> to activate your account.
      </p>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-3">
          <FormBanner />
          <Field name="password" type={show ? "text" : "password"} label="New password" autoComplete="new-password"
            labelClassName={labelCls} inputClassName={inputCls} rightAdornment={eye} autoFocus
            hint="At least 8 characters; avoid common or all-number passwords."
            rules={{ required: "Choose a password.", minLength: { value: 8, message: "Use at least 8 characters." } }} />
          <Field name="confirm_password" type={show ? "text" : "password"} label="Confirm password" autoComplete="new-password"
            labelClassName={labelCls} inputClassName={inputCls}
            rules={{
              required: "Confirm your password.",
              validate: (v) => v === getValues("password") || "Passwords don't match.",
            }} />
          <button type="submit" disabled={saving}
            className="mt-3 w-full py-3 rounded-xl text-white font-semibold shadow-lg shadow-violet-200 hover:opacity-90 transition-all disabled:opacity-60"
            style={{ background: GRADIENT }}>
            {saving ? "Activating…" : "Set password & activate"}
          </button>
        </form>
      </FormProvider>
    </Card>
  );
};

export default AcceptInvite;
